
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../Components/Table/Table";
import TableLoading from "../../../Components/Loader/TableLoading";
import { API_BASE_URL } from "../../../config/api";

const NETWORK_OWNER_URL =
  `${API_BASE_URL}payment-payout/network-owner/networks/me/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;

const formatUsd = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return "-";
  }
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return value ?? "-";
  }
  return amount.toLocaleString();
};

const formatPackageBreakdown = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }

    return value
      .map((item) => {
        if (item && typeof item === "object") {
          const key =
            item.package_name ??
            item.package ??
            item.name ??
            item.key ??
            item.id ??
            "item";
          const count = item.count ?? item.value ?? item.total ?? "";
          return count === "" ? `${key}` : `${key}: ${count}`;
        }
        return `${item}`;
      })
      .join(" | ");
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) {
      return "-";
    }
    return entries.map(([key, count]) => `${key}: ${count}`).join(" | ");
  }

  return `${value}`;
};

const buildNetworkRows = (network) => [
  { label: "شناسه شبکه", value: network?.id ?? "-" },
  { label: "نام شبکه", value: network?.name ?? "-" },
  { label: "کد شبکه", value: network?.code ?? "-" },
  { label: "وضعیت شبکه", value: network?.is_active ? "فعال" : "غیرفعال" },
  { label: "کل فروش شبکه", value: formatUsd(network?.entire_sale_usd) },
  {
    label: "تعداد کل چالش فروخته‌شده",
    value: formatNumber(network?.entire_number_of_challenges_sold),
  },
  {
    label: "تفکیک فروش چالش‌ها بر اساس پکیج",
    value: formatPackageBreakdown(
      network?.entire_number_of_challenges_by_package,
    ),
  },
  {
    label: "مجموع سود مشارکت‌کنندگان",
    value: formatUsd(network?.total_profit_participants_made),
  },
  {
    label: "تعداد مشارکت‌کنندگان پرداخت‌شده",
    value: formatNumber(network?.total_participants_paid_out),
  },
  {
    label: "تعداد مشارکت‌کنندگان قابل برداشت",
    value: formatNumber(network?.total_participants_can_payout),
  },
  {
    label: "کل کاربران واردشده از شبکه",
    value: formatNumber(network?.total_users_joined_via_network),
  },
];

const buildOwnerRows = (owner) => [
  { label: "شناسه کاربر", value: owner?.user_id ?? "-" },
  { label: "نام", value: owner?.name ?? "-" },
  { label: "ایمیل", value: owner?.email ?? "-" },
  { label: "فروش مستقیم", value: formatUsd(owner?.direct_sales_usd) },
  {
    label: "تعداد چالش‌های مستقیم",
    value: formatNumber(owner?.direct_number_of_challenges),
  },
  {
    label: "تفکیک چالش‌های مستقیم بر اساس پکیج",
    value: formatPackageBreakdown(
      owner?.direct_number_of_challenges_by_package,
    ),
  },
  {
    label: "تعداد چالش‌های غیرمستقیم",
    value: formatNumber(owner?.indirect_number_of_challenges),
  },
  {
    label: "تفکیک چالش‌های غیرمستقیم بر اساس پکیج",
    value: formatPackageBreakdown(
      owner?.indirect_number_of_challenges_by_package,
    ),
  },
  { label: "سود کل مستقیم", value: formatUsd(owner?.entire_profit_direct) },
  {
    label: "سود کل غیرمستقیم",
    value: formatUsd(owner?.entire_profit_indirect),
  },
  { label: "سود کل", value: formatUsd(owner?.entire_profit_total) },
  { label: "سود برداشت‌شده", value: formatUsd(owner?.entire_profit_payed_out) },
  {
    label: "سود قابل برداشت",
    value: formatUsd(owner?.entire_profit_can_payout),
  },
  {
    label: "کاربران جذب‌شده مستقیم",
    value: formatNumber(owner?.entire_users_joined_directly),
  },
];

export default function NetworkOwnerSection() {
  const navigate = useNavigate();
  const [networkOwnerData, setNetworkOwnerData] = useState(null);
  const [accLoading, setAccLoading] = useState(true);

  const fetchNetworkOwnerData = async () => {
    try {
      let response = await fetch(NETWORK_OWNER_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      if (response.status === 401) {
        try {
          const refreshResponse = await fetch(REFRESH_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          });

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            navigate("/signin");
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(NETWORK_OWNER_URL, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          });
        } catch (err) {
          console.error("Error refreshing token:", err);
          setAccLoading(false);
          return;
        }
      }

      if (response.status === 403 || response.status === 404) {
        setNetworkOwnerData(null);
        setAccLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      setNetworkOwnerData(data);
      setAccLoading(false);
    } catch (error) {
      setAccLoading(false);
      console.error("Error fetching network owner data:", error);
    }
  };

  useEffect(() => {
    fetchNetworkOwnerData();
  }, []);

  if (accLoading) {
    return (
      <div className="w-full p-5 flex items-center justify-center gap-2 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark">
        <p className="text-[#808080]">در حال بارگذاری اطلاعات شبکه</p>
        <TableLoading className="text-[#808080]" />
      </div>
    );
  }

  if (
    !networkOwnerData ||
    !networkOwnerData.network ||
    !networkOwnerData.owner
  ) {
    return null;
  }

  const bloggers = Array.isArray(networkOwnerData.bloggers)
    ? networkOwnerData.bloggers
    : [];

  const networkRows = buildNetworkRows(networkOwnerData.network);
  const ownerRows = buildOwnerRows(networkOwnerData.owner);

  return (
    <div className="w-full p-5 flex flex-col gap-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark">
      <div className="w-full flex items-center justify-between">
        <h3 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          اطلاعات شبکه همکاری
        </h3>
      </div>

      <div className="w-full flex flex-col gap-3">
        <h4 className="text-base font-medium text-zi-text-light dark:text-zi-text-dark">
          اطلاعات شبکه
        </h4>
        <Table
          isEnglish={[1]}
          columns={[
            { key: "label", label: "شاخص" },
            { key: "value", label: "مقدار" },
          ]}
          data={networkRows}
        />
      </div>

      <div className="w-full flex flex-col gap-3">
        <h4 className="text-base font-medium text-zi-text-light dark:text-zi-text-dark">
          اطلاعات مالک شبکه
        </h4>
        <Table
          isEnglish={[1]}
          columns={[
            { key: "label", label: "شاخص" },
            { key: "value", label: "مقدار" },
          ]}
          data={ownerRows}
        />
      </div>

      <div className="w-full flex flex-col gap-3">
        <h4 className="text-base font-medium text-zi-text-light dark:text-zi-text-dark">
          لیست بلاگرها
        </h4>
        <Table
          isEnglish={[0, 2, 3, 5, 6, 7]}
          columns={[
            { key: "userId", label: "شناسه کاربر" },
            { key: "name", label: "نام" },
            { key: "email", label: "ایمیل" },
            { key: "soldCount", label: "تعداد چالش فروخته‌شده" },
            { key: "soldByPackage", label: "تفکیک فروش بر اساس پکیج" },
            { key: "profitMade", label: "سود ایجادشده" },
            { key: "profitPayout", label: "سود برداشت‌شده" },
            { key: "profitCanPayout", label: "سود قابل برداشت" },
          ]}
          data={bloggers.map((blogger) => ({
            userId: blogger?.user_id ?? "-",
            name: blogger?.name ?? "-",
            email: blogger?.email ?? "-",
            soldCount: formatNumber(blogger?.entire_challenges_count_sold),
            soldByPackage: formatPackageBreakdown(
              blogger?.entire_challenges_count_by_package,
            ),
            profitMade: formatUsd(blogger?.profit_made),
            profitPayout: formatUsd(blogger?.profit_payed_out),
            profitCanPayout: formatUsd(blogger?.profit_can_payout),
          }))}
        />
        {bloggers.length === 0 && (
          <p className="w-full text-center text-sm text-zi-text-light/60 dark:text-zi-text-dark/60">
            بلاگری برای نمایش وجود ندارد
          </p>
        )}
      </div>
    </div>
  );
}
